<template>
  <div class="toolbar-buttons" aria-label="Chat response toolbar">
    <ChatbotCopyResponseButton :trace-id="traceId" />
    <ChatbotGoodResponseButton :active="lastFeedback === 1" @select="sendFeedback(1)" />
    <ChatbotBadResponseButton
      v-model:comment="comment"
      :active="lastFeedback === 0"
      @select="sendFeedback(0, $event)"
    />
  </div>
</template>

<script>
// The newer @langfuse/client package doesn't yet support client-side user feedback: https://github.com/langfuse/langfuse/issues/10546
import { LangfuseWeb } from 'langfuse';
import { langfusePublicKey } from '@/app.config';

// Create langfuse once, instead of for each instance in script setup.
const langfuse = new LangfuseWeb({
  publicKey: langfusePublicKey,
  baseUrl: 'https://langfuse.tree.com',
});
</script>

<script setup>
// Every component in this folder is named .ce.vue, including the buttons below that aren't
// custom elements themselves. That extension is what compiles a component's styles into the
// component instead of into the page's <head>, letting Vue inject them into this custom
// element's shadow root - the only place a style can reach the toolbar's markup.
import { ref } from 'vue';
import ChatbotBadResponseButton from './ChatbotBadResponseButton.ce.vue';
import ChatbotCopyResponseButton from './ChatbotCopyResponseButton.ce.vue';
import ChatbotGoodResponseButton from './ChatbotGoodResponseButton.ce.vue';

const { traceId } = defineProps({
  traceId: String,
});

// Tracks which feedback button was last clicked (1 = good, 0 = bad, null = none).
const lastFeedback = ref(null);

// Held here, rather than in the bad response button, so it can be cleared once sent.
const comment = ref('');

// Map VUE_APP_EVEREST_ENV values to environment strings used by Tonquin.API
const environmentMap = {
  production: 'prod',
  staging: 'stage',
  local: 'development',
};

async function sendFeedback(score, feedbackComment) {
  if (!traceId) {
    console.error('Cannot send feedback due to missing trace ID.');
    return;
  }
  // Clicking the same button again should not send duplicate feedback, unless
  // a new comment is being added to an already-submitted bad rating.
  if (lastFeedback.value === score && !feedbackComment) {
    return;
  }
  lastFeedback.value = score;
  await langfuse.score({
    traceId,
    // Use <product>.<object>.<metric> naming scheme
    name: 'chat.response.quality',
    value: score,
    comment: feedbackComment,
    environment: environmentMap[process.env.VUE_APP_EVEREST_ENV] || process.env.VUE_APP_EVEREST_ENV,
  });
  comment.value = '';
}
</script>

<style>
:host {
  display: block;
}

.toolbar-buttons {
  display: flex;
  align-items: center;
}

.toolbar-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: content-box;
  width: 20px;
  height: 20px;
  padding: 6px;
  color: var(--chatbot-icon-color);
  background: none;
  border-style: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 160ms ease-out;

  &:hover,
  &:focus-visible {
    background: var(--chatbot-toolbar-icon-hover-background);
  }

  &:focus-visible {
    outline: 2px solid var(--chatbot-focus-ring-color);
    outline-offset: 1px;
  }

  &:active {
    background: var(--chatbot-toolbar-icon-active-background);
  }
}

.toolbar-button--good {
  &:hover,
  &:focus-visible {
    color: var(--chatbot-positive-color);
  }

  &:active {
    color: var(--chatbot-positive-color-pressed);
  }

  &.toolbar-button--active {
    color: var(--chatbot-positive-color);

    & .toolbar-icon {
      animation: feedback-pop 500ms cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    &:hover,
    &:focus-visible {
      color: var(--chatbot-positive-color-selected-hover);
    }
  }
}

.toolbar-button--bad {
  &:hover,
  &:focus-visible {
    color: var(--chatbot-negative-color);
  }

  &:active {
    color: var(--chatbot-negative-color-pressed);
  }

  &.toolbar-button--active {
    color: var(--chatbot-negative-color);

    & .toolbar-icon {
      animation: feedback-pop 500ms cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    &:hover,
    &:focus-visible {
      color: var(--chatbot-negative-color-selected-hover);
    }
  }
}

@keyframes feedback-pop {
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.5);
  }
  100% {
    transform: scale(1);
  }
}

.toolbar-icon {
  display: inline-flex;
}

.toolbar-icon svg {
  width: 18px;
  height: 18px;
  fill: currentColor;
}
</style>
